/**
 * Global reactive flag: true while the driver has an active booking in the
 * "approach" phases (assigned / driver_reached / in_progress).
 *
 * Why this exists: `drivers.is_available` flips to false the moment a driver
 * accepts a booking (see dispatch-trip `accept` action), and `activeTripStore`
 * only populates at `in_progress`. Between acceptance and trip start neither
 * signal is true, so `useDriverHeartbeat` — which publishes
 * `drivers.last_location_lat/lng` (the customer map's primary marker source) —
 * stopped firing during the driver-approaching phase. This store closes that
 * gap: DriverHome (the source of truth for the active booking) writes it, and
 * the DriverAuthProvider reads it to keep the heartbeat running.
 *
 * Survives reloads via localStorage so a cold-start mid-approach keeps
 * publishing until DriverHome re-confirms the booking status.
 */
const STORAGE_KEY = 'driver_approach_booking_v1';
const EVENT_NAME = 'driver:approach-booking-changed';

let cachedValue = false;

function read(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    cachedValue = raw === '1';
    return cachedValue;
  } catch {
    cachedValue = false;
    return false;
  }
}

function write(active: boolean) {
  cachedValue = active;
  try {
    if (active) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch { /* quota / private mode */ }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  } catch { /* SSR guard */ }
}

export const approachBookingStore = {
  get(): boolean {
    // Return the cached value; lazily initialise from storage on first read.
    if (cachedValue) return true;
    return read();
  },
  set(active: boolean) {
    if (active === read()) return;
    write(active);
  },
  clear() {
    if (read() === false) return;
    write(false);
  },
  subscribe(listener: () => void): () => void {
    const onCustom = () => listener();
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) listener();
    };
    window.addEventListener(EVENT_NAME, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  },
};
